import React, { useState, useEffect } from "react";
import {
  UseFormRegister,
  FieldErrors,
  Control,
  useWatch,
  Controller,
} from "react-hook-form";
import Input from "../../../components/FormInputs";
import CustomButton from "../../../components/AddButton";
import FormTitle from "../../../components/FormTitle";
import CustomModal from "../../../components/Modal";
import PhoneInput from "../../../components/phoneinput";
import CustomDropDown from "../../../components/Dropdown/CustomDropDown";
import { ApiService } from "../../../service/api.service";
import { OPERTIONS_URL } from "../../../common/common";
import CustomModalDropDown from "../../../components/Dropdown/CustomModalDropDown";
import { Plus } from "../../../common/svg";

interface ClientDetailsProps {
  errors: FieldErrors;
  register: UseFormRegister<any>;
  handleAdd?: () => void;
  setValue: any;
  control: Control<any>;
  setconsignorDetailsData: (data: any) => void;
}

const ConsigneeDetails: React.FC<ClientDetailsProps> = ({
  setconsignorDetailsData,
  errors,
  register,
  control,
  setValue,
}) => {
  const apiService1 = new ApiService();
  const entityId = localStorage.getItem("entity_id");
  const [CompanyName, setCompanyName] = useState<any>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<any>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [Executive, setExecutive] = useState<any>([]);
  const [forms, setForms] = useState<number[]>([0]); // Start with 0 for consistency
  const [useSameContact, setUseSameContact] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isCompanyModalOpen, setCompanyModalOpen] = useState(false);
  const [formIdToRemove, setFormIdToRemove] = useState<number | null>(null);
  const [currentFormIndex, setCurrentFormIndex] = useState<number>(0);
  const [options, setOptions] = useState<any[]>([]);
  const [executivesByCompany, setExecutivesByCompany] = useState<
    Record<string, any[]>
  >({});
  const [executiveData, setExecutiveData] = useState<any>({});
  // Add this state to track the last selected executive for each form
  const [lastExecutiveSelections, setLastExecutiveSelections] = useState<
    Record<number, string>
  >({});
  // Track last selected company to prevent loop
  const [lastCompanySelections, setLastCompanySelections] = useState<
    Record<number, string>
  >({});
  // Add this state to track whether fields have been manually edited
  const [manuallyEdited, setManuallyEdited] = useState<Record<string, boolean>>(
    {}
  );
  // Add flag to track if executive fetch is in progress
  const [fetchingExecutives, setFetchingExecutives] = useState<Record<number, boolean>>({});

  // Helper function to get error for a specific field
  const getFieldError = (index: number, fieldName: string) => {
    return (
      (errors.consignorDetails as Record<number, any>)?.[index]?.[fieldName]
        ?.message || ""
    );
  };

  // Helper function to check if field has error
  const hasFieldError = (index: number, fieldName: string) => {
    return !!(errors.consignorDetails as Record<number, any>)?.[index]?.[
      fieldName
    ];
  };

  const openModal = (formId: number) => {
    setFormIdToRemove(formId);
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const openCompanyModal = (index: number) => {
    setCurrentFormIndex(index);
    setFilteredCompanies(CompanyName);
    setSearchTerm("");
    setCompanyModalOpen(true);
  };

  const closeCompanyModal = () => setCompanyModalOpen(false);

  const handleCompanySearch = (value: string) => {
    setSearchTerm(value);

    if (value.trim() === "") {
      setFilteredCompanies(CompanyName);
    } else {
      const filtered = CompanyName.filter((company: any) =>
        company.label.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCompanies(filtered);
    }
  };

  const handleAddForm = () => {
    setForms((prevForms) => [...prevForms, prevForms.length]);
    setUseSameContact(false);
  };

  const handleConfirmRemove = () => {
    if (formIdToRemove !== null) {
      setForms((prevForms) => prevForms.filter((id) => id !== formIdToRemove));
      setFormIdToRemove(null);
      closeModal();
    }
  };

  const watchFieldsArray = useWatch({
    control,
    name: forms
      .map((index) => [
        `consignorDetails[${index}].companyName`,
        `consignorDetails[${index}].executive`,
        `consignorDetails[${index}].contactNumber`,
        `consignorDetails[${index}].emailId`,
      ])
      .flat(),
  }) as string[];

  const watchFields = Object.fromEntries(
    forms
      .map((index) => [
        [`consignorDetails[${index}].companyName`, watchFieldsArray[index * 4] || ""],
        [`consignorDetails[${index}].executive`, watchFieldsArray[index * 4 + 1] || ""],
        [`consignorDetails[${index}].contactNumber`, watchFieldsArray[index * 4 + 2] || ""],
        [`consignorDetails[${index}].emailId`, watchFieldsArray[index * 4 + 3] || ""],
      ])
      .flat()
  );

  const fetchExecutives = async (companyId: string, formIndex: number) => {
    // Prevent duplicate fetches for the same company
    if (fetchingExecutives[formIndex] || !companyId) {
      return;
    }

    // Don't fetch if the company ID hasn't changed
    if (lastCompanySelections[formIndex] === companyId) {
      return;
    }

    try {
      // Mark fetch as in progress
      setFetchingExecutives(prev => ({...prev, [formIndex]: true}));
      
      // Record this as the last selected company
      setLastCompanySelections(prev => ({...prev, [formIndex]: companyId}));
      
      const response: any = await apiService1.getApi(
        `${OPERTIONS_URL}/executive/details-route?clientId=${companyId}&entityId=${entityId}`
      );
      
      if (response.status === "200" && response.body) {
        // Format executives data
        const formattedExecutives = response.body.map((item: any) => ({
          value: item.executiveId,
          label: `${item.firstName} ${item.lastName || ""}`,
          emailId: item.emailId,
          phoneNumber: item.phoneNumber,
        }));
        
        // Update executive data for this form
        setExecutiveData((prev:any) => ({
          ...prev,
          [formIndex]: formattedExecutives,
        }));
        
        // If there's only one executive, auto-select it
        if (response.body.length === 1) {
          const executive = response.body[0];
          setValue(
            `consignorDetails[${formIndex}].executive`,
            executive.executiveId
          );
          
          // Only set email and phone if not manually edited
          if (!manuallyEdited[`consignorDetails[${formIndex}].emailId`]) {
            setValue(
              `consignorDetails[${formIndex}].emailId`,
              executive.emailId
            );
          }
          
          if (!manuallyEdited[`consignorDetails[${formIndex}].contactNumber`]) {
            setValue(
              `consignorDetails[${formIndex}].contactNumber`,
              executive.phoneNumber
            );
          }
          
          // Mark this selection as coming from auto-select
          setLastExecutiveSelections(prev => ({
            ...prev,
            [formIndex]: executive.executiveId,
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch executives", error);
    } finally {
      // Mark fetch as complete
      setFetchingExecutives(prev => ({...prev, [formIndex]: false}));
    }
  };

  // Function to handle manual edits to fields
  const handleManualEdit = (fieldName: string) => {
    setManuallyEdited(prev => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  // Update parent component with form data
  useEffect(() => {
    const updatedConsignorDetails = forms.map((index) => {
      const companyNameField = `consignorDetails[${index}].companyName`;
      const executiveField = `consignorDetails[${index}].executive`;
      const contactNumberField = `consignorDetails[${index}].contactNumber`;
      const emailIdField = `consignorDetails[${index}].emailId`;

      return {
        companyName: watchFields[companyNameField] || "",
        executive: watchFields[executiveField] || "",
        contactNumber: watchFields[contactNumberField] || "",
        countryCode: "+91", // Defaults to "+91"
        emailId: watchFields[emailIdField] || "",
      };
    });
    
    // Ensure all fields are properly included before sending to parent
    const validDetails = updatedConsignorDetails.filter(detail => 
      detail.companyName || detail.executive || detail.contactNumber || detail.emailId
    );
    
    setconsignorDetailsData(validDetails.length > 0 ? validDetails : updatedConsignorDetails);
  }, [watchFieldsArray, forms, setconsignorDetailsData]);

  // Auto-fill email and phone when executive changes
  useEffect(() => {
    forms.forEach((formIndex) => {
      const executiveField = `consignorDetails[${formIndex}].executive`;
      const executiveValue = watchFields[executiveField];
      const emailField = `consignorDetails[${formIndex}].emailId`;
      const phoneField = `consignorDetails[${formIndex}].contactNumber`;

      // Only auto-fill if executive has changed and we have data
      if (
        executiveValue && 
        executiveValue !== lastExecutiveSelections[formIndex] && 
        executiveData[formIndex]
      ) {
        // Update last selection
        setLastExecutiveSelections(prev => ({
          ...prev,
          [formIndex]: executiveValue,
        }));

        // Find executive data
        const selectedExecutive = executiveData[formIndex]?.find(
          (exec: any) => exec.value === executiveValue
        );

        // Auto-fill if found and not manually edited
        if (selectedExecutive) {
          if (!manuallyEdited[emailField]) {
            setValue(emailField, selectedExecutive.emailId);
          }
          if (!manuallyEdited[phoneField]) {
            setValue(phoneField, selectedExecutive.phoneNumber);
          }
        }
      }
    });
  }, [watchFieldsArray, forms, executiveData, setValue, manuallyEdited, lastExecutiveSelections]);

  // Fetch executives when company changes - with debounce
  useEffect(() => {
    const companyChangeTimers: Record<number, NodeJS.Timeout> = {};

    forms.forEach((formIndex) => {
      const companyField = `consignorDetails[${formIndex}].companyName`;
      const companyValue = watchFields[companyField];
      
      if (companyValue && companyValue !== "" && companyValue !== lastCompanySelections[formIndex]) {
        // Clear any pending timer
        if (companyChangeTimers[formIndex]) {
          clearTimeout(companyChangeTimers[formIndex]);
        }
        
        // Set a new timer
        companyChangeTimers[formIndex] = setTimeout(() => {
          fetchExecutives(companyValue, formIndex);
        }, 300); // 300ms debounce
      }
    });

    // Cleanup timers
    return () => {
      Object.values(companyChangeTimers).forEach(timer => clearTimeout(timer));
    };
  }, [watchFields, forms, lastCompanySelections]);

  return (
    <div className="bg-white p-4 rounded-[20px]">
      <div className="flex items-center justify-between ">
        <FormTitle title="Consignor Details" />
        <CustomButton label="" onClick={handleAddForm} icon={Plus} />
      </div>
      <div className="">
        <div className="space-y-8">
          {forms.map((formId, index) => (
            <div key={formId}>
              <div className="flex items-center justify-end mb-2 gap-4">
                <p className="text-[14px] font-[600]">
                  #{(index + 1).toString().padStart(2, "0")}
                </p>
                {index >= 1 && (
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => openModal(formId)}
                      type="button"
                      className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                      aria-label="Remove"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 p-6 bg-white border-b border-dashed"
              >
                {/* Company Name */}
                <div className="flex flex-col">
                      <CustomModalDropDown
                        label="Company Name"
                        name={`consignorDetails[${index}].companyName`}
                        endpoint={`client/get-all-route`}
                        register={register}
                        fetchExecutives={fetchExecutives}
                        setValue={(name: string, value: string) => {
                          setValue(name, value);
                          // No need to call fetchExecutives here - the useEffect will handle it
                        }}
                        
                        errors={
                          hasFieldError(index, "companyName")
                            ? {
                                [`consignorDetails[${index}].companyName`]: {
                                  message: getFieldError(index, "companyName"),
                                },
                              }
                            : {}
                        }
                        defaultValue={watchFields[`consignorDetails[${index}].companyName`] || ""}
                      />
                </div>

                <Controller
                  name={`consignorDetails[${index}].executive`}
                  control={control}
                  render={({ field }) => (
                    <CustomDropDown
                      label="Executive Name"
                      name={`consignorDetails[${index}].executive`}
                      options={executiveData[index] || options}
                      register={register}
                      errors={
                        hasFieldError(index, "executive")
                          ? {
                              [`consignorDetails[${index}].executive`]: {
                                message: getFieldError(index, "executive"),
                              },
                            }
                          : {}
                      }
                      defaultValue={field.value || ""}
                      onChange={(value) => {
                        field.onChange(value);
                        // Clear manual edit flags when executive changes
                        const emailField = `consignorDetails[${index}].emailId`;
                        const phoneField = `consignorDetails[${index}].contactNumber`;
                        setManuallyEdited(prev => ({
                          ...prev,
                          [emailField]: false,
                          [phoneField]: false
                        }));
                      }}
                    />
                  )}
                />

                {/* Phone Number */}
                <div className="flex flex-col">
                  <PhoneInput
                    label="Phone Number"
                    name={`consignorDetails[${index}].contactNumber`}
                    defaultCountry="IN"
                    type="tel"
                    maxLength={10}
                    placeholder="Enter your phone number"
                    control={control}
                    errors={
                      hasFieldError(index, "contactNumber")
                        ? {
                            [`consignorDetails[${index}].contactNumber`]: {
                              message: getFieldError(index, "contactNumber"),
                            },
                          }
                        : {}
                    }
                    onChange={() =>
                      handleManualEdit(
                        `consignorDetails[${index}].contactNumber`
                      )
                    }
                  />
                </div>

                {/* Mail ID */}
                <div className="flex flex-col">
                  <Input
                    label="Mail ID"
                    name={`consignorDetails[${index}].emailId`}
                    register={register}
                    placeholder="Enter Mail ID"
                    errors={
                      hasFieldError(index, "emailId")
                        ? {
                            [`consignorDetails[${index}].emailId`]: {
                              message: getFieldError(index, "emailId"),
                            },
                          }
                        : {}
                    }
                    disabled={index > 0 && useSameContact}
                    onChange={() =>
                      handleManualEdit(`consignorDetails[${index}].emailId`)
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <CustomModal
        isOpen={isModalOpen}
        content={
          <div className="flex flex-col items-center">
            <p className="text-[18px] font-[600] text-[#4B4B4B]">
              Are you sure you want to remove this form?
            </p>
            <div className="mt-4 flex gap-4">
              <button
                onClick={handleConfirmRemove}
                type="button"
                className="bg-red-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                Yes
              </button>
              <button
                onClick={closeModal}
                type="button"
                className="bg-gray-500 rounded-[5px] h-[37px] w-[99px] text-white"
              >
                No
              </button>
            </div>
          </div>
        }
        onClose={closeModal}
      />
    </div>
  );
};

export default ConsigneeDetails;